import { useEffect, useRef, useState } from 'react';

/**
 * UI_REFERENCE_SOURCE: Sakhtar Topbar Popovers.dc.html — "TOPBAR POPOVER
 * BEHAVIOR" spec: single-open (opening one closes any other), close on
 * outside click or Esc. Ported from the board's own `Component` class
 * (`componentDidMount`'s `mousedown`/`keydown` listeners + `tog(k)`).
 *
 * `containerRef` must be attached to the element that wraps every trigger +
 * panel pair (mirrors the board's `[data-popwrap]` scope) — a click inside
 * any of them never counts as "outside".
 */
export function usePopoverState<K extends string>() {
  const [open, setOpen] = useState<K | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open === null) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (containerRef.current && target && containerRef.current.contains(target)) return;
      setOpen(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(null);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (key: K) => () => setOpen((current) => (current === key ? null : key));
  /** Opens `key` unconditionally — for triggers with their own focus event
   * (the search box) where toggling on every refocus would immediately
   * close what focus just opened. */
  const openOnly = (key: K) => () => setOpen(key);
  const close = () => setOpen(null);

  return { open, toggle, openOnly, close, containerRef };
}
